import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles, ShieldCheck, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SECTIONS, BUNDLE } from "@/data/sections";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/brand";

const EUCALYPTUS = "https://images.pexels.com/photos/8903651/pexels-photo-8903651.jpeg";
const FAMILY = "https://images.unsplash.com/photo-1513267000941-598abe7be16f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SHOWCASE = "https://images.unsplash.com/photo-1648822145238-16e13a1591da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const start = () => {
    if (!user) navigate("/register");
    else if (!user.is_paid) navigate("/pricing");
    else navigate("/planner");
  };

  return (
    <div className="min-h-screen bg-ivory text-charcoal">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-ivory/80 border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img src={LOGO_URL} alt="The Ever Grace Group" className="h-14 w-14 rounded-full object-cover" />
            <span className="font-serif text-2xl text-navy tracking-tight">
              <span className="text-navy">Ever</span> <span className="text-gold">Grace</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-9 text-sm text-navy/80">
            <a href="#sections" className="hover:text-gold transition-colors" data-testid="nav-sections">Sections</a>
            <a href="#bundle" className="hover:text-gold transition-colors" data-testid="nav-bundle">What's Included</a>
            <Link to="/book" className="hover:text-gold transition-colors" data-testid="nav-book">Book a Consultation</Link>
            <a href="#about" className="hover:text-gold transition-colors" data-testid="nav-about">About</a>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={start} data-testid="nav-start-btn"
                className="bg-navy hover:bg-navy/90 text-ivory rounded-full px-6">
                My Legacy
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm text-navy hover:text-gold transition-colors" data-testid="nav-signin">Sign in</Link>
                <Button onClick={start} data-testid="nav-start-btn"
                  className="bg-navy hover:bg-navy/90 text-ivory rounded-full px-6">
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="eucalyptus-corner top-0 left-0" style={{ backgroundImage: `url(${EUCALYPTUS})`, transform: "scaleX(-1)" }} />
        <div className="eucalyptus-corner bottom-0 right-0" style={{ backgroundImage: `url(${EUCALYPTUS})` }} />
        <div className="max-w-3xl mx-auto px-6 py-24 md:py-32 text-center relative">
          <motion.img src={LOGO_URL} alt="The Ever Grace Group"
            variants={fade} initial="hidden" animate="show" custom={0}
            className="w-40 h-40 md:w-52 md:h-52 mx-auto mb-6 rounded-full object-cover" />
          <motion.p variants={fade} initial="hidden" animate="show" custom={1}
            className="uppercase tracking-[0.35em] text-xs text-sage mb-6">
            The Ever Grace Group Presents
          </motion.p>
          <motion.h1 variants={fade} initial="hidden" animate="show" custom={2}
            className="font-serif text-5xl md:text-7xl text-navy leading-[1.05]">
            Peace of Mind<br />Legacy
          </motion.h1>
          <motion.div variants={fade} initial="hidden" animate="show" custom={3}
            className="w-16 h-px bg-gold mx-auto my-8" />
          <motion.p variants={fade} initial="hidden" animate="show" custom={3}
            className="text-lg text-charcoal/80 max-w-xl mx-auto">
            Your complete life, estate &amp; final wishes vault — thoughtfully designed to help
            families prepare with confidence and love.
          </motion.p>
          <motion.div variants={fade} initial="hidden" animate="show" custom={4}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={start} data-testid="hero-start-btn"
              className="bg-gold hover:bg-gold/90 text-navy rounded-full px-8 py-6 text-base font-semibold group">
              Begin Your Legacy
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" onClick={() => document.getElementById("bundle").scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-bundle-btn"
              className="rounded-full px-8 py-6 text-base border-navy/20 text-navy hover:bg-navy/5">
              See What's Included
            </Button>
          </motion.div>
        </div>
      </header>

      {/* Trust strip */}
      <section className="border-y border-gold/20 bg-white/50">
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {[
            { icon: ShieldCheck, t: "Private & secure", d: "Your information stays yours." },
            { icon: Sparkles, t: "Compassionate AI help", d: "Gentle guidance when words are hard." },
            { icon: FileDown, t: "Export to PDF", d: "A beautiful keepsake for your family." },
          ].map((f, i) => (
            <div key={i} className="flex items-center justify-center gap-3">
              <f.icon className="w-6 h-6 text-sage" strokeWidth={1.5} />
              <div className="text-left">
                <p className="font-serif text-lg text-navy leading-tight">{f.t}</p>
                <p className="text-sm text-charcoal/70">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About / intro */}
      <section id="about" className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16 items-center">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <p className="uppercase tracking-[0.3em] text-xs text-sage mb-4">A gift of preparation</p>
          <h2 className="font-serif text-4xl md:text-5xl text-navy leading-tight mb-6">
            The most loving thing you can leave behind is clarity.
          </h2>
          <p className="text-charcoal/80 text-lg leading-relaxed mb-4">
            When everything is written down — accounts, wishes, passwords, and the words that matter —
            your family is free to grieve, remember, and heal, without scrambling for answers.
          </p>
          <p className="text-charcoal/70 leading-relaxed">
            Peace of Mind gently guides you through nine essential sections,
            one thoughtful worksheet at a time.
          </p>
        </motion.div>
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
          className="relative">
          <div className="absolute -inset-3 border border-gold/40 rounded-md" />
          <img src={FAMILY} alt="A peaceful family moment"
            className="rounded-md w-full h-[420px] object-cover shadow-[0_20px_50px_rgba(27,42,71,0.12)]" />
        </motion.div>
      </section>

      {/* Sections */}
      <section id="sections" className="bg-white/60 border-y border-gold/20 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="uppercase tracking-[0.3em] text-xs text-sage mb-4">Nine thoughtful sections</p>
            <h2 className="font-serif text-4xl md:text-5xl text-navy">Everything, organized with care</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTIONS.map((s, i) => (
              <motion.div key={s.id} variants={fade} initial="hidden" whileInView="show"
                viewport={{ once: true }} custom={i % 3}
                data-testid={`section-card-${s.id}`}
                className="group bg-ivory border border-gold/20 rounded-md p-8 hover:border-gold/60 hover:shadow-[0_16px_40px_rgba(27,42,71,0.08)] transition-[border-color,box-shadow,transform] hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-11 h-11 rounded-full bg-sage/15 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-navy" strokeWidth={1.5} />
                  </span>
                  <span className="font-serif text-xs text-gold">0{i + 1}</span>
                </div>
                <h3 className="font-serif text-2xl text-navy mb-2">{s.title}</h3>
                <p className="text-sm text-charcoal/70 leading-relaxed">{s.tagline}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundle */}
      <section id="bundle" className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="uppercase tracking-[0.3em] text-xs text-sage mb-4">The complete bundle</p>
          <h2 className="font-serif text-4xl md:text-5xl text-navy leading-tight mb-6">
            More than a workbook — a complete toolkit
          </h2>
          <p className="text-charcoal/80 text-lg leading-relaxed mb-8">
            Everything a family needs, beautifully prepared and ready to print or fill in digitally.
          </p>
          <div className="relative">
            <div className="absolute -inset-3 border border-gold/40 rounded-md" />
            <img src={SHOWCASE} alt="The printed keepsake"
              className="rounded-md w-full h-[320px] object-cover shadow-[0_20px_50px_rgba(27,42,71,0.12)]" />
          </div>
        </div>
        <div className="space-y-4">
          {BUNDLE.map((b, i) => (
            <motion.div key={i} variants={fade} initial="hidden" whileInView="show"
              viewport={{ once: true }} custom={i % 4}
              data-testid={`bundle-item-${i}`}
              className="flex gap-4 bg-white border border-gold/20 rounded-md p-5">
              <span className="mt-1 w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                <Check className="w-3.5 h-3.5 text-navy" strokeWidth={2.5} />
              </span>
              <div>
                <h4 className="font-serif text-xl text-navy leading-tight">{b.name}</h4>
                <p className="text-sm text-charcoal/70">{b.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="eucalyptus-corner top-0 right-0" style={{ backgroundImage: `url(${EUCALYPTUS})`, opacity: 0.18 }} />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center relative">
          <h2 className="font-serif text-4xl md:text-6xl mb-6">Begin today. Rest easy tomorrow.</h2>
          <p className="text-ivory/80 text-lg mb-10 max-w-xl mx-auto">
            Start with a single section. Your progress saves automatically, and you can return anytime.
          </p>
          <Button onClick={start} data-testid="cta-start-btn"
            className="bg-gold hover:bg-gold/90 text-navy rounded-full px-10 py-6 text-base font-semibold group">
            Begin Your Legacy
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      <footer className="bg-ivory border-t border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="The Ever Grace Group" className="h-12 w-12 rounded-full object-cover" />
            <span className="font-serif text-lg text-navy">
              <span className="text-navy">Ever</span> <span className="text-gold">Grace</span> Group
            </span>
          </div>
          <p className="text-sm text-charcoal/60">Your life. Your wishes. Your legacy.</p>
        </div>
      </footer>
    </div>
  );
}
